import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  lessonsService,
  LessonId,
  LessonsProgress,
  LessonStatus,
  LESSONS_PROGRESS_CHANGED_EVENT,
} from '../services/lessonsService';

export interface UseLessonsProgressReturn {
  progress: LessonsProgress;
  isAllCompleted: boolean;
  isAllResolved: boolean;
  completeLesson: (lessonId: LessonId) => void;
  skipLesson: (lessonId: LessonId) => void;
  skipAllRemaining: () => void;
  isLessonCompleted: (lessonId: LessonId) => boolean;
  getLessonStatus: (lessonId: LessonId) => LessonStatus;
}

/**
 * useLessonsProgress
 *
 * 封裝 lessonsService，提供 React state 驅動的 Lessons 進度管理
 */
export function useLessonsProgress(): UseLessonsProgressReturn {
  const [progress, setProgress] = useState<LessonsProgress>(() =>
    lessonsService.getProgress()
  );

  // 監聽同 tab 內其他元件（如 Lesson Guide）直接呼叫 lessonsService 方法後的通知
  useEffect(() => {
    const handleChange = () => setProgress(lessonsService.getProgress());
    window.addEventListener(LESSONS_PROGRESS_CHANGED_EVENT, handleChange);
    return () => window.removeEventListener(LESSONS_PROGRESS_CHANGED_EVENT, handleChange);
  }, []);

  const completeLesson = useCallback((lessonId: LessonId) => {
    lessonsService.markCompleted(lessonId);
    // markCompleted 已 dispatch event，但 hook 自身也直接更新，避免異步延遲
    setProgress(lessonsService.getProgress());
  }, []);

  const skipLesson = useCallback((lessonId: LessonId) => {
    lessonsService.markSkipped(lessonId);
    setProgress(lessonsService.getProgress());
  }, []);

  const skipAllRemaining = useCallback(() => {
    lessonsService.markAllSkipped();
    setProgress(lessonsService.getProgress());
  }, []);

  const isLessonCompleted = useCallback(
    (lessonId: LessonId) => progress[lessonId].status === 'completed',
    [progress]
  );

  const getLessonStatus = useCallback(
    (lessonId: LessonId) => progress[lessonId].status,
    [progress]
  );

  const isAllCompleted = useMemo(
    () =>
      progress['lesson-1'].status === 'completed' &&
      progress['lesson-2'].status === 'completed' &&
      progress['lesson-3'].status === 'completed',
    [progress]
  );

  const isAllResolved = useMemo(
    () =>
      progress['lesson-1'].status !== 'not_started' &&
      progress['lesson-2'].status !== 'not_started' &&
      progress['lesson-3'].status !== 'not_started',
    [progress]
  );

  return {
    progress,
    isAllCompleted,
    isAllResolved,
    completeLesson,
    skipLesson,
    skipAllRemaining,
    isLessonCompleted,
    getLessonStatus,
  };
}
